import { useState } from "react";

import dayjs from "dayjs";
import PropTypes from "prop-types";
import { FormProvider, useFieldArray, useForm, useWatch } from "react-hook-form";
import { ulid } from "ulid";

import { yupResolver } from "@hookform/resolvers/yup";
import {
  Divider,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid";

import { AddProductVariantSection, CenteredLoading } from "@/features/ui";
import { GeneralInformationSection } from "./general-information-section";
import {
  DEFAULT_VALUES,
  VALIDATION_SCHEMA,
} from "./goods-receipt-form-constants";
import { ProductVariantsSection } from "./product-variants-section";
import { SelectPurchaseOrderProductVariantSection } from "./select-purchase-order-product-variant-section";

export const GoodsReceiptForm = ({
  defaultValues,
  onSubmit,
  renderActionButton,
  editMode = false,
  isLoading = false,
}) => {
  const [inputMethod, setInputMethod] = useState("purchase-order");

  const handleInputMethodChange = (_, newMode) => {
    if (newMode !== null) {
      setInputMethod(newMode);
    }
  };

  const methods = useForm({
    defaultValues: defaultValues
      ? { ...DEFAULT_VALUES, ...defaultValues }
      : DEFAULT_VALUES,
    resolver: yupResolver(VALIDATION_SCHEMA),
  });
  const { control, handleSubmit, formState } = methods;

  const [buyer, seller, sellerableType] = useWatch({
    control,
    name: ["buyer", "seller", "sellerableType"],
  });

  // Browse the catalogue as whoever has to be able to sell the product. On an
  // internal receipt the seller is a HUB outlet and the backend rejects lines
  // it can't access, so listing as the buyer offers products that fail on
  // submit. An external supplier has no product scope — fall back to the buyer.
  const productOutletId =
    sellerableType === "OUTLET" ? seller?.id : buyer?.id;

  const { append } = useFieldArray({
    control,
    name: "goodsReceiptProductVariants",
  });

  const onFormSubmit = async (formData) => {
    const payload = {
      transactionDate: dayjs(formData.transactionDate).format("YYYY-MM-DD"),
      warehouseId: formData.warehouse.id,
      buyerId: formData.buyer.id,
      ...(!editMode && {
        sellerableId: formData.seller.id,
        sellerableType: formData.sellerableType,
      }),
      sellerReferenceNumber: formData.sellerReferenceNumber,
      note: formData.note || null,
      goodsReceiptProductVariants: formData.goodsReceiptProductVariants.map(
        (variant) => ({
          ...(editMode && variant.id ? { id: variant.id } : {}),
          purchaseOrderProductVariantId: variant.purchaseOrderProductVariantId,
          sku: variant.sku,
          quantity: variant.quantity,
          note: variant.note || null,
        })
      ),
    };

    await onSubmit(payload);
  };

  return isLoading ? (
    <CenteredLoading sx={{ height: "100vh" }} />
  ) : (
    <FormProvider {...methods}>
      <Grid
        container
        component="form"
        noValidate
        onSubmit={handleSubmit(onFormSubmit)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
          }
        }}
        spacing={2.5}
      >
        <Grid size={{ xs: 12 }}>
          <GeneralInformationSection editMode={editMode} />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Paper sx={{ p: 2.5, borderRadius: 2 }}>
            <Stack spacing={2.5}>
              <Stack
                direction="row"
                sx={{
                  justifyContent: "space-between",
                  alignItems: "center"
                }}>
                <Typography variant="h6">Ambil Barang</Typography>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={inputMethod}
                  onChange={handleInputMethodChange}
                >
                  <ToggleButton value="purchase-order">Pesanan</ToggleButton>
                  <ToggleButton value="manual">Manual</ToggleButton>
                </ToggleButtonGroup>
              </Stack>
              {inputMethod === "purchase-order" ? (
                <SelectPurchaseOrderProductVariantSection />
              ) : (
                <AddProductVariantSection
                  outletId={productOutletId}
                  onAdded={(productVariant, product, quantity) => {
                    append({
                      id: ulid(),
                      productVariantId: productVariant.id,
                      name: productVariant.name,
                      sku: productVariant.sku,
                      unitOfMeasurement: product.unitOfMeasurement.name,
                      quantity,
                      maxQuantity: null, // Manual entries have no max limit
                      invoicedQuantity: 0,
                      returnedQuantity: 0,
                    });
                  }}
                />
              )}
              <Divider />
              <Typography variant="h6">Penerimaan Barang</Typography>
              <ProductVariantsSection />
            </Stack>
          </Paper>
        </Grid>
        <Grid
          size={{ xs: 12 }}
          sx={{
            display: "flex",
            justifyContent: "flex-end"
          }}>
          {renderActionButton({ isSubmitting: formState.isSubmitting })}
        </Grid>
      </Grid>
    </FormProvider>
  );
};

GoodsReceiptForm.propTypes = {
  defaultValues: PropTypes.object,
  onSubmit: PropTypes.func,
  renderActionButton: PropTypes.func,
  editMode: PropTypes.bool,
  isLoading: PropTypes.bool,
};
